import { useEffect, useState } from "react";
import {
  fetchAthleteTargets,
  fetchMaxesDetailed,
  parseProgramSummaries,
  type AthleteTargets,
  type CoachVisibleProfile,
  type CurrentMax,
  type ProgramRow,
} from "@heraion/shared";

import type { ProgressApiFetch } from "../Progress/types";
import { useAthleteSessions, type AthleteSessionsList } from "./useAthleteSessions";

// CO-9 athlete-detail data hook. Fetches ONLY what the detail surface needs
// beyond the panels' own reads: the athlete's profile projection (units /
// weekStartDay / pinned id), their programs (for the pinned block +
// prescription) and their current MAX map — all through the CO-11 edge-gated
// routes. The session-results + catalog reads are deliberately NOT here: the
// shared Progress panels fetch those themselves through the athlete-scoped
// ProgressDeps (one single-flight pair per athlete), so this hook never
// duplicates useCoachDashboard's fan-out.
//
// Fail-soft like the dashboard (CO-8b): a missing profile (athlete never
// opened Settings) or a hiccuping read degrades to safe defaults instead of
// sinking the whole screen.
export type AthleteDetailState = {
  loading: boolean;
  profile: CoachVisibleProfile | null;
  // A program another coach wrote comes as its summary (#332).
  programs: ReadonlyArray<ProgramRow>;
  maxes: Map<string, CurrentMax> | null;
  // ME-3: the athlete's own AthleteTargets (GET /api/me/targets?athleteSub) —
  // the coach scores the athlete's real goals, not the pinned program's
  // embedded targets. null while loading OR when the read failed.
  athleteTargets: AthleteTargets | null;
  /** #25: the athlete's sessions since the bond began, paged. */
  sessions: AthleteSessionsList;
};

export function useAthleteDetail(
  apiFetch: ProgressApiFetch,
  athleteSub: string,
): AthleteDetailState {
  const sessions = useAthleteSessions(apiFetch, athleteSub);
  const [state, setState] = useState<Omit<AthleteDetailState, "sessions">>({
    loading: true,
    profile: null,
    programs: [],
    maxes: null,
    athleteTargets: null,
  });

  useEffect(() => {
    let alive = true;
    setState({
      loading: true,
      profile: null,
      programs: [],
      maxes: null,
      athleteTargets: null,
    });

    (async () => {
      const q = `athleteSub=${encodeURIComponent(athleteSub)}`;
      const [profile, programs, maxes, athleteTargets] = await Promise.all([
        apiFetch<CoachVisibleProfile>(
          `/api/profiles/${encodeURIComponent(athleteSub)}`,
        ).catch(() => null),
        apiFetch<unknown>(`/api/me/programs?${q}`)
          .then(parseProgramSummaries)
          .catch(() => [] as ProgramRow[]),
        fetchMaxesDetailed(apiFetch, athleteSub).catch(
          () => new Map<string, CurrentMax>(),
        ),
        fetchAthleteTargets(apiFetch, athleteSub),
      ]);
      if (!alive) return;
      setState({ loading: false, profile, programs, maxes, athleteTargets });
    })();

    return () => {
      alive = false;
    };
    // `athleteSub` is the only real input — apiFetch is a stable per-surface
    // seam (and where the host scopes it per athlete, its identity tracks the
    // sub anyway). Widening the deps would re-fetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteSub]);

  return { ...state, sessions };
}
